import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";

const SIDE = "https://images.pexels.com/photos/9148280/pexels-photo-9148280.jpeg?auto=compress&cs=tinysrgb&h=1200";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="eyebrow !text-neutral-500">{label}</span>
    <input className="lux-input" {...props} />
  </label>
);

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: params.get("role") || "hostess", company: "", city: "", telegram: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      toast.success(mode === "login" ? `Bentornata/o, ${u.name.split(" ")[0]}` : "Account creato con successo");
      nav(u.role === "admin" ? "/admin" : mode === "register" && u.role === "hostess" ? "/profilo" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grid lg:grid-cols-2 min-h-[calc(100vh-5rem)]" data-testid="auth-page">
      <Seo title="Accedi o registrati | YourHostess" noindex />
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="" className="absolute inset-0 w-full h-full object-cover grayscale-[30%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/30 to-transparent" />
        <p className="absolute bottom-12 left-12 font-serif text-4xl max-w-sm leading-tight">Eleganza, puntualità, <em className="text-[#D4AF37]">presenza.</em></p>
      </div>
      <div className="flex items-center px-6 lg:px-20 py-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-7 fade-up" data-testid="auth-form">
          <div className="flex gap-8 border-b border-white/10">
            {[["login", "Accedi"], ["register", "Registrati"]].map(([m, l]) => (
              <button type="button" key={m} onClick={() => { setMode(m); setError(""); }}
                className={`pb-3 text-xs uppercase tracking-[0.2em] -mb-px border-b transition-colors ${mode === m ? "border-[#D4AF37] text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"}`}
                data-testid={`auth-tab-${m}`}>{l}</button>
            ))}
          </div>
          <h1 className="font-serif text-4xl">{mode === "login" ? "Bentornata/o" : "Crea il tuo account"}</h1>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3" data-testid="auth-role-selector">
              {[["hostess", "Sono una Hostess / Modella"], ["client", "Sono un Cliente / Azienda"]].map(([r, l]) => (
                <button type="button" key={r} onClick={() => setForm({ ...form, role: r })}
                  className={`p-4 text-left text-sm border transition-colors ${form.role === r ? "border-[#D4AF37] bg-[#D4AF37]/5 text-white" : "border-white/10 text-neutral-400 hover:border-white/30"}`}
                  data-testid={`auth-role-${r}`}>{l}</button>
              ))}
            </div>
          )}

          {mode === "register" && <Field label="Username" value={form.name} onChange={set("name")} required placeholder="Il nome visibile sul tuo profilo" data-testid="auth-name-input" />}
          {mode === "register" && form.role === "client" && <Field label="Azienda (opzionale)" value={form.company} onChange={set("company")} data-testid="auth-company-input" />}
          {mode === "register" && form.role === "hostess" && <Field label="Città" value={form.city} onChange={set("city")} placeholder="Es. Milano" data-testid="auth-city-input" />}
          {mode === "register" && form.role === "hostess" && <Field label="Username Telegram *" value={form.telegram} onChange={set("telegram")} placeholder="@tuo_username" required minLength={5} data-testid="auth-telegram-input" />}
          <Field label="Email" type="email" value={form.email} onChange={set("email")} required data-testid="auth-email-input" />
          <Field label="Password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="auth-password-input" />

          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
          <button disabled={loading} className="btn-gold w-full" data-testid="auth-submit-button">
            {loading ? "Attendere…" : mode === "login" ? "Accedi" : "Crea account"}
          </button>
        </form>
      </div>
    </main>
  );
}
